import React, { useEffect, useState } from "react";
import {
  MotifErrorMessage,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import Button from "@/components/Form/Button/Button";
import { REQUIRED_MESSAGE } from "@/Constant";
import { AddDebtorFormType } from "./type";
import "./FormModal.scss";
import { DEBTOR_DESCRIPTION, LEGAL_DEBTOR_NAME } from "./constants";
import ControlInputAddDebtor from "./ControlInputAddDebtor";
import { isEmptyOrSpaces } from "@/utility/validation";
import ControlTextAreaWithCharLimit from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import {
  CANCEL_BUTTON_VARIANT,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";

type Props = {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setPostData: (data: AddDebtorFormType, create: boolean) => void;
  create: boolean;
  formData?: AddDebtorFormType | null;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
  setFormData: any;
};

export default function FormModal({
  isVisible,
  setIsVisible,
  setPostData,
  create,
  formData,
  setShowError,
  setFormData,
}: Props) {
  const { t } = useTranslation();
  const [showMessage, setShowMessage] = useState({
    show: false,
    type: "error",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    reset,
  } = useForm<AddDebtorFormType>({
    defaultValues: {
      legalDebtorName: "",
      debtorDescription: "",
    },
  });

  const onSubmit = async (data: AddDebtorFormType) => {
    const { legalDebtorName, debtorDescription } = data;

    if (
      isEmptyOrSpaces(legalDebtorName) ||
      isEmptyOrSpaces(debtorDescription)
    ) {
      setShowMessage({
        show: true,
        type: "error",
        message: t(
          `${REQUIRED_MESSAGE} Legal Debtor Name, Debtor Description cannot be empty or contain only spaces.`
        ),
      });
      return;
    }

    setIsLoading(true);
    setShowMessage({ show: false, type: "error", message: "" }); // Clear previous error message
    setPostData(data, create);
    setIsVisible(false);
    setValue(LEGAL_DEBTOR_NAME, "");
    setValue(DEBTOR_DESCRIPTION, "");
    setIsLoading(false);
    setShowError(false);
  };
  useEffect(() => {
    reset({
      legalDebtorName: formData?.legalDebtorName || "",
      debtorDescription: formData?.debtorDescription || "",
    });
  }, [formData,reset]);
  return (
    <>
      <MotifModal
        onClose={() => {
          setFormData(null);
          setIsVisible(false);
          reset();
        }}
        show={isVisible}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="formModalWrapper">
            <MotifModalHeader>
              <span className="discard_heading">
                {t("pages.caseSetup.debtorInformation.formLabels.addDebtor")}
              </span>
            </MotifModalHeader>
            <MotifModalBody style={{ paddingTop: "0px" }}>
              <div className="formModal">
                <div className="motif-col-md-12 heading"></div>
                <div className="motif-col-md-12 mar-b-30">
                  <div className="form-field">
                    <ControlInputAddDebtor
                      name={LEGAL_DEBTOR_NAME}
                      control={control}
                      isTextArea={false}
                      label={
                        "pages.caseSetup.debtorInformation.formLabels.debtorName"
                      }
                      showLabel={true}
                      placeholder={t(
                        "pages.caseSetup.debtorInformation.formLabels.legalNameDebtor"
                      )}
                      required={true}
                    />
                  </div>
                  <MotifErrorMessage>
                    {errors?.legalDebtorName?.type === "required" ? (
                      <>{t("pages.caseSetup.debtorInformation.debtorName")}</>
                    ) : (
                      <></>
                    )}
                  </MotifErrorMessage>
                </div>
                <div className="motif-col-md-12 mar-b-10">
                  <div className="form-field">
                    <ControlTextAreaWithCharLimit
                      name={DEBTOR_DESCRIPTION}
                      control={control}
                      label={
                        "pages.caseSetup.debtorInformation.formLabels.description"
                      }
                      isTextArea={true}
                      showLabel={true}
                      required={true}
                    />
                  </div>
                  <MotifErrorMessage>
                    {errors?.debtorDescription?.type === "required" ? (
                      <>
                        {t(
                          "pages.caseSetup.debtorInformation.debtorDescription"
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </MotifErrorMessage>
                </div>
              </div>
              {showMessage.show ? (
                <div>
                  <MotifErrorMessage>
                    {showMessage.show && showMessage.message}
                  </MotifErrorMessage>
                </div>
              ) : (
                ""
              )}
            </MotifModalBody>
            <MotifModalFooter>
              <div className="button-container-add">
                <Button
                  label={t("global.globalActions.save")}
                  variant={SAVE_BUTTON_VARIANT}
                  disabled={isLoading}
                />
                <Button
                  label={t("global.globalActions.cancel")}
                  type="button"
                  variant={CANCEL_BUTTON_VARIANT}
                  className="cancelBtn"
                  onClickHandler={() => {
                    setFormData(null);
                    setIsVisible(false);
                    reset();
                  }}
                />
              </div>
            </MotifModalFooter>
          </div>
        </form>
      </MotifModal>
    </>
  );
}
